import { URL_PARSE_BASE } from "@/constants/domains";

const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

/**
 * Server-decided next best action for the current user.
 * `rationale` is internal-only — it must NEVER be rendered to users.
 */
export interface NextStepResponse {
  stepKey: string;
  ctaIntent: string;
  rationale?: string;
  ctaUrl: string;
  ctaLabel: string;
}

function isValidNextStep(data: unknown): data is NextStepResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.stepKey === "string" && typeof d.ctaUrl === "string" && typeof d.ctaLabel === "string"
  );
}

/**
 * Fetch the server's next-best-action for the current user.
 * Returns null on any failure (non-OK, network error, malformed body) so
 * callers can hide next-step surfaces entirely.
 */
export async function fetchNextStep(): Promise<NextStepResponse | null> {
  try {
    const resp = await fetch(withBase("/users/me/next-step"), {
      method: "GET",
      credentials: "include",
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return isValidNextStep(data) ? data : null;
  } catch {
    return null;
  }
}

// ── Module-level cache so multiple consumers (sidebar card, tip strip,
// welcome tips) on the same route share one fetch. A new key (route
// change) triggers a refetch. ──
let cached: { key: string; value: NextStepResponse | null } | null = null;
let inflight: { key: string; promise: Promise<NextStepResponse | null> } | null = null;

/**
 * Load the next step, deduped per `key` (typically the current pathname).
 * Repeat calls with the same key reuse the cached result; a different key
 * refetches (route change = fresh server decision).
 */
export function loadNextStep(key: string): Promise<NextStepResponse | null> {
  if (cached && cached.key === key) return Promise.resolve(cached.value);
  if (inflight && inflight.key === key) return inflight.promise;

  const promise = fetchNextStep().then(value => {
    cached = { key, value };
    if (inflight && inflight.key === key) inflight = null;
    return value;
  });
  inflight = { key, promise };
  return promise;
}

/** Reset the shared cache (used by tests and logout flows). */
export function clearNextStepCache(): void {
  cached = null;
  inflight = null;
}

/**
 * Convert the server's absolute ctaUrl into an in-app relative path
 * (pathname + search) for client-side navigation.
 */
export function toRelativeCtaPath(ctaUrl: string): string {
  if (!ctaUrl) return "/";
  try {
    // Already-relative in-app paths pass through; anything else must be an
    // absolute URL or we fall back to root.
    const url = ctaUrl.startsWith("/") ? new URL(ctaUrl, URL_PARSE_BASE) : new URL(ctaUrl);
    return `${url.pathname}${url.search}`;
  } catch {
    return "/";
  }
}
